import { channel } from 'redux-saga';
import { put, takeEvery, take, fork, call, select, actionChannel, all, cancel } from 'redux-saga/effects';
import { subscribe, unsubscribe } from '@push-component/client';
import { BYO_PRICE_UPDATES } from '@push-component/constants';
import { noticeError } from '../../utils/noticeError';
import * as constants from '../constants';
import { addPrice, updatePrice, addSelectionToProcessingQueue, addSelectionToStore, addSelectionIncompatibleError, removeSelectionFromProcessingQueue, removeSelectionFromStore, removePrice, processSelection, hideEventLevelError, setEditID, setLastTopic, marketFetchingStarted, saveMarkets, showEventLevelError } from '../actions';
import { publishSelectionAddAnalytics, publishSelectionRemoveAnalytics } from '../../components/analyticsHelpers';
import config from '../../config';
import { getPrice, getMarketsData } from '../../dataProviders';
import { PUSH_SUBSCRIBE_OWNER } from '../../constants';
const selectionAreIncompatible = (selA, selB) => selA.byoMarketType !== 'PLAYERLINEBASED'
    && selB.byoMarketType !== 'PLAYERLINEBASED'
    && selA.category === selB.category
    && selA.type
    && selB.type
    && selA.type === selB.type
    && selA.team === selB.team
    && selA.period === selB.period;
const subscribeChannel = channel();
let updateTask;
export function* cancelUpdateTask() {
    // @ts-ignore: Yield error
    const topic = yield select(state => state.slip.lastTopic);
    if (topic) {
        unsubscribe({
            topic,
            owner: PUSH_SUBSCRIBE_OWNER,
            type: BYO_PRICE_UPDATES
        });
    }
    if (updateTask && !updateTask.isCancelled()) {
        yield cancel(updateTask);
    }
}
export function* updatePriceSaga(priceSubscription) {
    const topic = (priceSubscription === null || priceSubscription === void 0 ? void 0 : priceSubscription.topic) ? priceSubscription.topic : null;
    if (!topic) {
        return;
    }
    yield put(setLastTopic(topic));
    subscribe({
        topic,
        owner: PUSH_SUBSCRIBE_OWNER,
        type: BYO_PRICE_UPDATES,
        callback: subscribeChannel.put
    });
    while (true) {
        // @ts-ignore: Yield error
        const action = yield take(subscribeChannel);
        yield put(updatePrice(action.data));
    }
}
export function* processSelectionSaga(action) {
    const selectionToAdd = action.payload;
    try {
        // @ts-ignore: Yield error
        const selectionsInSlip = yield select(state => Object.keys(state.slip.selections)
            .map(key => state.slip.selections[key]));
        const incompatibleSelections = selectionsInSlip.filter((sel) => selectionAreIncompatible(selectionToAdd, sel));
        const newSelections = selectionsInSlip
            .filter((sel) => !incompatibleSelections.some((incompatible) => incompatible.id === sel.id))
            .concat(selectionToAdd);
        if (newSelections && newSelections.length >= config.minimumSelections) {
            // @ts-ignore: Yield error
            const eventId = yield select(state => state.markets.data.id);
            // @ts-ignore: Yield error
            const lastTopic = yield select(state => state.slip.lastTopic);
            // @ts-ignore: Yield error
            const response = yield call(getPrice, newSelections, eventId, lastTopic);
            yield put(addPrice(response.selection));
            // yield call(cancelUpdateTask);
            // // @ts-ignore: Yield error
            // updateTask = yield spawn(updatePriceSaga, response.priceSubscription);
        }
        yield put(addSelectionToStore(selectionToAdd));
        if (incompatibleSelections.length > 0) {
            yield all(incompatibleSelections.map((sel) => put(removeSelectionFromStore(sel))));
        }
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        publishSelectionAddAnalytics(selectionToAdd);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        yield put(addSelectionIncompatibleError(error));
        // yield call(cancelUpdateTask);
    }
}
export function* addSelectionSaga(action) {
    // @ts-ignore: Yield error
    const selectionsInSlip = yield select(state => Object.keys(state.slip.selections).length);
    // @ts-ignore: Yield error
    const selectionInProcess = yield select(state => state.slip.processingSelections.length);
    if (selectionsInSlip + selectionInProcess < config.maximumSelections) {
        yield put(addSelectionToProcessingQueue(action.payload));
        yield put(processSelection(action.payload));
    }
}
export function* removeSelectionSaga(action) {
    try {
        yield put(removeSelectionFromStore(action.payload));
        // @ts-ignore: Yield error
        const newSelections = yield select(state => Object.keys(state.slip.selections).filter(id => id !== action.payload.id).map(id => state.slip.selections[id]));
        // yield call(cancelUpdateTask);
        if (newSelections && newSelections.length >= config.minimumSelections) {
            // @ts-ignore: Yield error
            const eventId = yield select(state => state.markets.data.id);
            // @ts-ignore: Yield error
            const lastTopic = yield select(state => state.slip.lastTopic);
            // @ts-ignore: Yield error
            const response = yield call(getPrice, newSelections, eventId, lastTopic);
            yield put(addPrice(response.selection));
            // @ts-ignore: Yield error
            // updateTask = yield spawn(updatePriceSaga, response.priceSubscription);
        }
        else {
            yield put(removePrice());
        }
        publishSelectionRemoveAnalytics(action.payload);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(addSelectionIncompatibleError(error));
        // yield call(cancelUpdateTask);
    }
}
export function* processSelectionWatcher() {
    // @ts-ignore: Yield error
    const channelQueue = yield actionChannel(constants.BYO_PROCESS_SELECTION);
    while (true) {
        // @ts-ignore: Yield error
        const action = yield take(channelQueue);
        yield call(processSelectionSaga, action);
    }
}
export function* editSelectionsSaga(action) {
    try {
        const { data, id, eventId } = action.payload;
        const { byoSelectionDetails } = data;
        yield put(hideEventLevelError());
        yield put(marketFetchingStarted());
        // @ts-ignore: Yield error
        const marketsResponse = yield call(getMarketsData, config.locale, eventId, true);
        const selectionsIds = new Set(byoSelectionDetails.map(({ selectionId, handicap }) => handicap ? `${selectionId}_${handicap.toFixed(2).replace('.', '-')}` : selectionId));
        const selectedSelections = [];
        marketsResponse.byoMarketGroups.forEach((marketGroup) => {
            const players = marketGroup.players && marketGroup.players.length ? marketGroup.players : [undefined];
            const periods = marketGroup.periods && marketGroup.periods.length ? marketGroup.periods : [undefined];
            const teams = marketGroup.teams && marketGroup.teams.length ? marketGroup.teams : [undefined];
            players.forEach((player) => {
                periods.forEach((period) => {
                    teams.forEach((team) => {
                        let selections = [];
                        if (player && team && period && marketGroup.markets[team][period][player]) {
                            ({ selections } = marketGroup.markets[team][period][player]);
                        }
                        else if (team && period && marketGroup.markets[team][period]) {
                            ({ selections } = marketGroup.markets[team][period]);
                        }
                        else if (team && !period) {
                            ({ selections } = marketGroup.markets[team]);
                        }
                        else if (!team && period) {
                            ({ selections } = marketGroup.markets[period]);
                        }
                        selections.forEach((selection) => {
                            if (selectionsIds.has(selection.id)) {
                                selectedSelections.push(Object.assign({ category: marketGroup.category, categoryName: marketGroup.categoryName, team,
                                    period,
                                    player }, selection));
                            }
                        });
                    });
                });
            });
        });
        // @ts-ignore: Yield error
        const lastTopic = yield select(state => state.slip.lastTopic);
        // @ts-ignore: Yield error
        const priceResponse = yield call(getPrice, selectedSelections, marketsResponse.id, lastTopic);
        yield all(selectedSelections.map(selection => put(addSelectionToStore(selection))));
        yield put(addPrice(priceResponse.selection));
        // yield call(cancelUpdateTask);
        // @ts-ignore: Yield error
        // updateTask = yield spawn(updatePriceSaga, priceResponse.priceSubscription);
        yield put(setEditID(id));
        yield put(saveMarkets(marketsResponse));
    }
    catch (error) {
        yield put(showEventLevelError(error.toString()));
        // yield call(cancelUpdateTask);
    }
}
// export function* clearAllSaga() {
//   yield call(cancelUpdateTask);
// }
// export function* enablelUpdateTask() {
//   // @ts-ignore: Yield error
//   const topic = yield select(state => state.slip.lastTopic);
//   if (topic) {
//     // @ts-ignore: Yield error
//     updateTask = yield spawn(updatePriceSaga, { topic });
//   }
// }
export function* editSelectionsWatcher() {
    yield takeEvery(constants.BYO_EDIT_SELECTIONS, editSelectionsSaga);
}
export function* removeSelectionWatcher() {
    yield takeEvery(constants.BYO_REMOVE_SELECTION, removeSelectionSaga);
}
export function* addSelectionWatcher() {
    yield takeEvery(constants.BYO_ADD_SELECTION, addSelectionSaga);
}
// export function* startPriceUpdateSubscription() {
//   yield takeEvery(constants.BYO_SLIP_START_PRICE_UPDATE_SUSCRIPTION, enablelUpdateTask);
// }
// export function* stopPriceUpdateSubscription() {
//   yield takeEvery(constants.BYO_SLIP_STOP_PRICE_UPDATE_SUSCRIPTION, cancelUpdateTask);
// }
// export function* clearAllWatcher() {
//   yield takeEvery(constants.BYO_SLIP_CLEAR_ALL, clearAllSaga);
// }
const slipSagas = [
    fork(addSelectionWatcher),
    fork(removeSelectionWatcher),
    fork(processSelectionWatcher),
    fork(editSelectionsWatcher),
    // fork(startPriceUpdateSubscription),
    // fork(stopPriceUpdateSubscription),
    // fork(clearAllWatcher),
];
export default slipSagas;
